import { PathsV1VehicleUuidGetParametersQueryExtend } from "@/api-schema-enums";
import type { paths } from "@/api-schema";
import { useVehicleByUuidQuery } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import type { VehicleWithRegistrationDate } from "@/common/api/vehicle/vehicle.types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { VehicleFormFields } from "../_components/vehicle-form-fields";

const VEHICLE_FORM_EXTEND = [
  PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
  PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
  PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Company,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Brands,
  PathsV1VehicleUuidGetParametersQueryExtend.OwningBranch_Address,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Company,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Brands,
  PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Address,
  PathsV1VehicleUuidGetParametersQueryExtend.ExchangeEngine,
] as const;

interface UpdateViewParams {
  uuid: string;
}

export function UpdateView() {
  const { t } = useTranslation(["masterdata", "common"]);
  const params = useParams<UpdateViewParams>();
  const uuid = params.uuid;
  const vehicleQueryParams = useMemo(
    () => ({
      parameters: {
        extend: [...VEHICLE_FORM_EXTEND],
      },
    }),
    [],
  );
  const vehicleQuery = useVehicleByUuidQuery(uuid, vehicleQueryParams);

  return (
    <UuidExistenceWrapper<
      paths["/v1/vehicle/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/vehicle/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={uuid}
      query={useVehicleByUuidQuery}
      queryResult={vehicleQuery}
      queryParams={vehicleQueryParams}
      notFoundProps={{
        title: t("masterdata:VehicleNotFound"),
        message: t("masterdata:VehicleDoesNotExist"),
        description: t("masterdata:CheckVehicleIdOrContactSupport"),
        backTo: "~/masterdata/vehicle",
        backToLabel: t("masterdata:BackToVehicleOverview"),
      }}
    >
      {vehicleQuery.data ? (
        <UpdateViewContent uuid={uuid} vehicle={vehicleQuery.data as VehicleWithRegistrationDate} />
      ) : null}
    </UuidExistenceWrapper>
  );
}

function UpdateViewContent({ uuid, vehicle }: { uuid: string; vehicle: VehicleWithRegistrationDate }) {
  const { t } = useTranslation(["masterdata", "common"]);
  const pageTitle = t("masterdata:EditVehicle");
  const pageSubtitle = t("common:ReviewAndSaveChangesDescription");
  return (
    <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
      <VehicleFormFields key={uuid} mode="update" uuid={uuid} vehicle={vehicle} />
    </CenteredLayout>
  );
}
